import { type ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  TABLE_ROW_HEIGHT,
  TABLE_ROW_HEIGHT_CSS_VAR,
  resolveTableRowHeight,
} from '@/features/shared/components/tableSystem/tableConstants';
import { useProjectStatusPresentation } from '@/features/shared/hooks/useProjectStatusPresentation';
import { getEmptyStateMessage } from '@/shared/copy/emptyStates';
import { Box } from '@/ui/primitives';
import { DataTable } from './DataTable';
import { buildServerDataGridStateParams } from './grid/serverDataGridStateParams';
import { SkeletonDataGrid } from './grid/SkeletonDataGrid';
import { ServerDataTableFrame } from './ServerDataTableFrame';
import { buildServerDataTableGridProps } from './serverDataTableGridProps';
import { resolveServerDataTableStyles } from './serverDataTableStyles';
import { resolveServerDataTableViewState } from './serverDataTableViewState';
import { useServerDataTableGridModel } from './useServerDataTableGridModel';
import type { ServerDataTableProps } from './dataTableShared';
import type { GridValidRowModel, SxProps, Theme } from '@/ui/primitives';
import type { DataGridProps as MuiDataGridProps } from '@mui/x-data-grid';

type ResolveSkeletonRowCountArgs = {
  explicitRowCount?: number;
  effectiveRowCount: number;
  visibleRowCount: number;
  page: number;
  pageSize: number;
};

const resolveSkeletonRowCount = ({
  explicitRowCount,
  effectiveRowCount,
  visibleRowCount,
  page,
  pageSize,
}: ResolveSkeletonRowCountArgs): number => {
  if (explicitRowCount !== undefined) {
    return explicitRowCount;
  }

  if (effectiveRowCount > 0) {
    const pageStart = page * pageSize;
    const targetPageRowCount = effectiveRowCount - pageStart;
    if (targetPageRowCount > 0) {
      return Math.min(pageSize, targetPageRowCount);
    }
  }

  if (visibleRowCount > 0) {
    return visibleRowCount;
  }

  return pageSize;
};

export const ServerDataTable = <T extends GridValidRowModel>(
  props: ServerDataTableProps<T>,
): ReactElement => {
  const {
    queryPath,
    queryFn,
    initialData,
    columns,
    getRowId,
    pageSizeOptions,
    initialPageSize,
    height,
    noRowsText = getEmptyStateMessage('data.noResultsFound'),
    filteredNoRowsText,
    onRowsChange,
    onQueryMeta,
    onDataUpdatedAt,
    fixedFilters,
    search,
    initialSortModel: initialSortModelProp,
    initialFilterModel: initialFilterModelProp,
    queryKeyPrefix,
    onRefetch,
    onInvalidate,
    onRowClick,
    onCellClick,
    onColumnHeaderClick,
    onQueryInput,
    queryKey: providedQueryKey,
    isAuthenticated = true,
    organisationId,
    projectId,
    persistKey,
    syncStateToUrl = false,
    autoHeight = false,
    disableColumnMenu = true,
    disableColumnSorting = true,
    disableColumnResize = false,
    showColumnVerticalBorder,
    showCellVerticalBorder,
    showToolbar = false,
    showLastUpdated = false,
    getRowHeight,
    skeletonRowCount: skeletonRowCountProp,
    hidePaginationControls = false,
    interactionMode = 'interactive',
    disablePageSizeSelect = false,
    hideFooter = false,
    hideSkeletonFooter = false,
    skeletonCellPaddingY,
    skeletonRowHeight,
    skeletonGetRowHeight,
    showSkeletonOnFetch = true,
    showPaginationSkeletonWhenRowsVisible = true,
    preservePreviousData = false,
    columnVisibilityModel: columnVisibilityModelProp,
    columnVisibilityPresets,
    columnVisibilityPreset,
    refetchIntervalMs,
    staleTimeMs,
    refetchOnWindowFocus,
    refetchOnMount,
    active = true,
    externalLoading = false,
    sx,
    containerSx,
    gridProps,
  } = props;
  const [searchParams] = useSearchParams();
  const { groupErroredWithFailed } = useProjectStatusPresentation();
  const resolvedTableRowHeight = resolveTableRowHeight(groupErroredWithFailed);
  const hasExplicitNonStandardRowHeight =
    skeletonRowHeight !== undefined && skeletonRowHeight !== TABLE_ROW_HEIGHT;
  const resolvedDefaultRowHeight = hasExplicitNonStandardRowHeight
    ? skeletonRowHeight
    : resolvedTableRowHeight;
  const { containerStyles, dataGridSx } = resolveServerDataTableStyles({
    containerSx,
    sx,
  });
  const resolvedDataGridSx: SxProps<Theme> = [
    {
      [TABLE_ROW_HEIGHT_CSS_VAR]: `${resolvedDefaultRowHeight}px`,
    },
    ...(dataGridSx ? (Array.isArray(dataGridSx) ? dataGridSx : [dataGridSx]) : []),
  ];
  const stateParams = buildServerDataGridStateParams<T>({
    queryPath,
    queryFn,
    initialData,
    columns,
    pageSizeOptions,
    initialPageSize,
    height,
    noRowsText,
    filteredNoRowsText,
    onRowsChange,
    onQueryMeta,
    onDataUpdatedAt,
    fixedFilters,
    search,
    initialSortModel: initialSortModelProp,
    initialFilterModel: initialFilterModelProp,
    queryKeyPrefix,
    queryKey: providedQueryKey,
    restoreFilterModel: interactionMode !== 'static',
    onRefetch,
    onInvalidate,
    onQueryInput,
    isAuthenticated,
    organisationId,
    projectId,
    persistKey,
    syncStateToUrl,
    autoHeight,
    columnVisibilityModel: columnVisibilityModelProp,
    columnVisibilityPresets,
    columnVisibilityPreset,
    refetchIntervalMs,
    staleTimeMs,
    refetchOnWindowFocus,
    refetchOnMount,
    active,
    skeletonGetRowHeight,
    getRowHeight,
    preservePreviousData,
  });

  const {
    errorMessage,
    rowsData,
    rowCount,
    showInitialSkeleton,
    showFetchSkeleton,
    showPaginationSkeleton,
    queryIsFetching,
    queryIsFetched,
    lastUpdatedAt,
    resolvedPageSizeOptions,
    resolvedHeight,
    resolvedNoRowsText,
    resolvedSkeletonRowHeight,
    paginationModel,
    columnVisibilityModel,
    columnsWithWidths,
    captureScrollAnchor,
    filterModel,
    handleFilterModelChange,
    handlePaginationModelChange,
    handleSortModelChange,
    setColumnVisibilityModel,
    handleColumnWidthChange,
    sortModel,
    suppressFocusOutline,
  } = useServerDataTableGridModel<T>({
    stateParams,
    columns,
    interactionMode,
    onColumnHeaderClick,
  });

  const {
    effectiveErrorMessage,
    effectiveRowCount,
    effectiveRowsData,
    gridReadyState,
    showPlaceholderGrid,
    showSkeletonGrid,
    skeletonTotalRowCount,
  } = resolveServerDataTableViewState<T>({
    errorMessage,
    externalLoading,
    forcedLoadStateParam: searchParams.get('loadState'),
    queryIsFetched,
    queryIsFetching,
    rowCount,
    rowsData,
    showFetchSkeleton,
    showInitialSkeleton,
    showPaginationSkeleton,
    showPaginationSkeletonWhenRowsVisible,
    showSkeletonOnFetch,
  });
  const skeletonRowCount = resolveSkeletonRowCount({
    explicitRowCount: skeletonRowCountProp,
    effectiveRowCount,
    visibleRowCount: effectiveRowsData.length,
    page: paginationModel.page,
    pageSize: paginationModel.pageSize,
  });
  const resolvedSkeletonRowHeightValue = resolvedDefaultRowHeight;
  const emptyOverlayHeight = skeletonRowCount * resolvedSkeletonRowHeightValue;
  const resolvedViewGridProps = buildServerDataTableGridProps<T>({
    autoHeight,
    captureScrollAnchor,
    columnVisibilityModel,
    columns: columnsWithWidths,
    dataGridSx: resolvedDataGridSx,
    defaultRowHeight: resolvedDefaultRowHeight,
    disableColumnMenu,
    disableColumnResize,
    disableColumnSorting,
    disablePageSizeSelect,
    emptyOverlayHeight,
    effectiveErrorMessage,
    effectiveRowCount,
    effectiveRowsData,
    filterModel,
    getRowHeight,
    getRowId,
    gridProps: gridProps as Partial<MuiDataGridProps> | undefined,
    handleColumnWidthChange,
    handleFilterModelChange,
    handlePaginationModelChange,
    handleSortModelChange,
    hideFooter,
    hidePaginationControls,
    onCellClick,
    onColumnHeaderClick,
    onRowClick,
    paginationModel,
    queryIsFetching,
    resolvedNoRowsText,
    resolvedPageSizeOptions,
    setColumnVisibilityModel,
    showCellVerticalBorder,
    showColumnVerticalBorder,
    showToolbar,
    sortModel,
    suppressFocusOutline,
  });
  const dataTableGrid = <DataTable gridProps={resolvedViewGridProps} />;
  const skeletonGrid = (
    <SkeletonDataGrid<T>
      columns={columnsWithWidths}
      loadingRowCount={skeletonRowCount}
      totalRowCount={showSkeletonGrid ? undefined : skeletonTotalRowCount}
      pageSizeOptions={resolvedPageSizeOptions}
      pageSize={paginationModel.pageSize}
      paginationModel={paginationModel}
      rowHeight={resolvedSkeletonRowHeightValue}
      autoHeight={autoHeight}
      showToolbar={showToolbar}
      getRowId={getRowId}
      getRowHeight={resolvedSkeletonRowHeight}
      cellPaddingY={skeletonCellPaddingY}
      columnVisibilityModel={columnVisibilityModel}
      hideFooter={hideSkeletonFooter || hideFooter}
      sx={resolvedDataGridSx}
    />
  );
  return (
    <ServerDataTableFrame
      containerStyles={containerStyles}
      dataError={effectiveErrorMessage}
      dataState={gridReadyState}
      dataTable={dataTableGrid}
      hasRows={effectiveRowsData.length > 0}
      isFetching={queryIsFetching || externalLoading}
      lastUpdatedAt={lastUpdatedAt}
      placeholder={
        <Box
          aria-hidden="true"
          sx={{
            minWidth: 0,
            opacity: 0,
            pointerEvents: 'none',
            visibility: 'hidden',
            width: '100%',
          }}
        >
          {skeletonGrid}
        </Box>
      }
      resolvedHeight={resolvedHeight}
      showLastUpdated={showLastUpdated}
      showPlaceholderGrid={showPlaceholderGrid}
      showSkeletonGrid={showSkeletonGrid}
      skeleton={skeletonGrid}
      skeletonHeightAnchor={null}
    />
  );
};
